import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';

import { ApiService } from '../services/api.service';
import { extractList } from '../shared/utils/extract-list';

interface Department {
  id: number;
  name: string;
  head: string;
  employeeCount: number;
  budget: string;
  status: string;
}

@Component({
  selector: 'app-departments',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatDialogModule,
    MatDividerModule,
    MatSelectModule,
    MatTooltipModule
  ],
  templateUrl: './departments.component.html',
  styleUrl: './departments.component.scss'
})
export class DepartmentsComponent implements OnInit {

  displayedColumns: string[] = ['name', 'head', 'employees', 'budget', 'status', 'actions'];

  departments: Department[] = [];

  filterText = '';
  filteredDepartments: Department[] = [];

  constructor(
    private apiService: ApiService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadDepartments();
  }

  loadDepartments(): void {
    this.apiService.getHRDepartments().subscribe({
      next: (response) => {
        const data = extractList(response);
        this.departments = data.map((dept: any) => ({
          id: dept.id || Math.random(),
          name: dept.name || 'Unknown',
          head:
            (dept.manager
              ? `${dept.manager.firstName || ''} ${dept.manager.lastName || ''}`.trim()
              : '') ||
            dept.managerName ||
            'TBD',
          employeeCount: dept.employeeCount || 0,
          budget: dept.budget ? `$${dept.budget.toLocaleString()}` : 'N/A',
          status: dept.status || 'Active'
        }));
        this.filteredDepartments = [...this.departments];
      },
      error: () => {}
    });
  }

  filterDepartments(): void {
    const text = this.filterText.toLowerCase().trim();
    this.filteredDepartments = this.departments.filter(dept =>
      dept.name.toLowerCase().includes(text) ||
      dept.head.toLowerCase().includes(text)
    );
  }

  addDepartment(): void {
    const name = prompt('Enter department name:');
    if (!name) return;

    const head = prompt('Enter department head:') || 'TBD';
    const newDept: Department = {
      id: Date.now(),
      name,
      head,
      employeeCount: 0,
      budget: 'N/A',
      status: 'Active'
    };

    this.apiService.createHRDepartment(newDept).subscribe({
      next: () => {
        this.loadDepartments();
      },
      error: (error) => {
        console.error('Create Department Error:', error);
      }
    });
  }

  editDepartment(department: Department): void {
    const newName = prompt('Edit department name:', department.name);
    if (newName === null) return;
    const newHead = prompt('Edit department head:', department.head) || department.head;

    const updated = { ...department, name: newName || department.name, head: newHead };

    this.apiService.updateHRDepartment(department.id, updated).subscribe({
      next: () => {
        this.loadDepartments();
      },
      error: (error) => {
        console.error('Update Department Error:', error);
      }
    });
  }

  deleteDepartment(department: Department): void {
    if (confirm(`Are you sure you want to delete "${department.name}"?`)) {
      this.apiService.deleteHRDepartment(department.id).subscribe({
        next: () => {
          this.loadDepartments();
        },
        error: (error) => {
          console.error('Delete Department Error:', error);
        }
      });
    }
  }
}